import React, { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

declare global {
  interface Window {
    Intercom: any;
    intercomSettings: any;
  }
}

interface IntercomProviderProps {
  children: React.ReactNode;
}

const IntercomProvider: React.FC<IntercomProviderProps> = ({ children }) => {
  const appId = process.env.REACT_APP_INTERCOM_APP_ID;
  const cookieYesEnabled = !!process.env.REACT_APP_COOKIEYES_ID;
  const { isAuthenticated, user, getAccessTokenSilently } = useAuth0();
  
  
  // Track if we've already sent user data to prevent duplicates
  // const [userDataSent, setUserDataSent] = React.useState(false);

  useEffect(() => {
    if (!appId) {
      return;
    }

    // Initialize Intercom settings early so boot/update always have app_id
    window.intercomSettings = {
      app_id: appId,
      hide_default_launcher: false,
    };

    // Load Intercom script - now treated as necessary cookies for customer support
    const load = () => {
      const script = document.createElement('script');
      script.async = true;
      script.crossOrigin = 'anonymous';
      script.src = `https://widget.intercom.io/widget/${appId}`;
      script.onerror = () => {};
      document.head.appendChild(script);
      return script;
    };

    // hasAnalyticsConsent function removed - Intercom now uses necessary cookies

    let script: HTMLScriptElement | null = null;
    
    const loadIntercom = () => {
      if (script) return; // Already loaded
      // Intercom is now treated as necessary cookies - always load for customer support
      script = load();
      if (script) {
        script.onload = () => {
          if (window.Intercom) {
            window.Intercom('boot', window.intercomSettings);
          }
        };
      }
    };
    
    const unloadIntercom = () => {
      if (script) {
        if (window.Intercom) {
          window.Intercom('shutdown');
        }
        script.remove();
        script = null;
      }
    };
    
    // Initial load
    loadIntercom();
    
    // Intercom is now treated as necessary cookies - no consent changes needed
    // Always load for customer support functionality

    // Cleanup function
    return () => {
      if (window.Intercom) {
        window.Intercom('shutdown');
      }
    };
  }, [appId, cookieYesEnabled]);

  // Keep Intercom user context in sync with Auth0
  useEffect(() => {
    if (!appId) {
      return;
    }

    // Wait for Intercom to be available
    const checkIntercom = () => {
      if (!window || !window.Intercom) {
        setTimeout(checkIntercom, 100);
        return;
      }
      
      // Only attempt when authenticated, we have an email, and roles are present (prevents 401 spam during role propagation)
      if (isAuthenticated && user && (user as any)?.email) {
        const userId = (user as any)?.sub;
        const name = (user as any)?.name || (user as any)?.nickname || '';
        const email = (user as any)?.email || '';
        const roles = Array.isArray((user as any)?.['https://bespot.com/roles']) ? (user as any)['https://bespot.com/roles'] : [];

        if (!roles || roles.length === 0) {
          // Roles not present yet; skip network calls and recheck shortly
          setTimeout(checkIntercom, 1500);
          return;
        }

        // Try to fetch JWT token from backend for Messenger Security
        const updateWithJWT = async () => {
          try {
            // Get the Auth0 token for authentication
            const token = await getAccessTokenSilently();
            
            const resp = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/intercom/user-jwt`, { 
              headers: { Authorization: `Bearer ${token}` }
            });
            const intercom_user_jwt = resp?.data?.intercom_user_jwt;
            
            if (intercom_user_jwt) {
              // When using JWT, only send the JWT token - user data is inside the JWT
              const payload = { 
                app_id: appId, 
                intercom_user_jwt: intercom_user_jwt,
                hide_default_launcher: false 
              };
              window.Intercom('update', payload);
            } else {
              // Fallback: send user data directly if no JWT
              const payload = { 
                app_id: appId, 
                user_id: userId, 
                name, 
                email, 
                hide_default_launcher: false 
              };
              window.Intercom('update', payload);
            }
          } catch (error) {
            // If backend returns 401/403 while roles/tokens are refreshing, skip silently and retry later
            setTimeout(() => {
              try {
                const payload = { app_id: appId, user_id: userId, name, email, hide_default_launcher: false };
                window.Intercom('update', payload);
              } catch {}
            }, 1500);
          }
        };
        updateWithJWT();
      } else {
        // If logged out, clear any user context
        window.Intercom('update', { app_id: appId, hide_default_launcher: false });
      }
    };

    checkIntercom();
  }, [appId, isAuthenticated, user, cookieYesEnabled]);

  return <>{children}</>;
};

export default IntercomProvider;
